
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';

import { Map, View } from 'ol';
import { defaults as defaultInteractions, DragZoom } from 'ol/interaction';
import { platformModifierKeyOnly } from 'ol/events/condition';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import {Fill, Stroke, Style} from 'ol/style.js';
import WKT from 'ol/format/WKT';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';

import 'ol/ol.css';

import { createLayer, createLayers, createProjection } from './olUtils';
import { createCircle, createStyles } from './olStyles';

import { Log } from '../../core';
const log = Log.getLogger('MultiSceneForApp.openlayers.OpenLayersMap');

export default class OpenLayersMap {

    constructor(options) {
        this._options = options || {};
        this._tempLayer = null;
        this._highlightLayer = null;
        this._measureLayer = null;
        this._map = null;
        this._view = null;
    }

    addInteractions() {
        this._map.addInteraction(new DragZoom({
            condition: platformModifierKeyOnly
        }));

        if (isArray(this._options.interactions)) {
            this._options.interactions.forEach(item => {
                let interactionOptions = {
                    measureLayer: this._measureLayer,
                    tempLayer: this._tempLayer,
                    onMapToolExecute: this._options.onMapToolExecute
                };
                let createInteractionFunc = (isArray(item))
                    ? item[0]
                    : item;
                if (isArray(item)) {
                    interactionOptions = {
                        ...interactionOptions,
                        ...item[1]
                    };
                }
                this._map.addInteraction(
                    createInteractionFunc(interactionOptions)
                );
            });
        }
    }

    createFeatureFromWkt(wkt, properties) {
        let feature = new Feature({
            geometry: this.createGeometryFromWkt(wkt)
        });
        feature.setProperties(properties);
        return feature;
    }

    createGeometryFromWkt(wkt) {
        let format = new WKT();
        return format.readGeometry(wkt);
    }

    getRgbaColorFromColor(color, alpha=1.0) {
        return `rgb(${color}, ${alpha})`;
    }

    createHighlightLayer() {

        let stylesDefinition = get(this._options.appConfig, 'map.highlightStyles');
        if (!stylesDefinition) {
            let color = get(this._options.appConfig, 'map.highlightColor');
            if (!color) {
                color = '0, 0, 255';
            } else {
                log.debug('use highlight color from project definition');
            }

            log.debug('use default highlight styles');
            stylesDefinition = [{
                type: 'circle',
                color: this.getRgbaColorFromColor(color, 0.5),
                width: 3,
                radius: 10,
                outlineColor: this.getRgbaColorFromColor(color, 0.5),
            }, {
                type: 'polygon',
                color: this.getRgbaColorFromColor(color, 0.2),
                width: 5,
                outlineColor: this.getRgbaColorFromColor(color, 0.5)
            }];
        } else {
            log.debug('use highlight styles from project definition');
        }

        let styles = createStyles(stylesDefinition);

        this._highlightLayer = new VectorLayer({
            id: '_HIGHLIGHT_',
            source: new VectorSource(),
            style: styles
        });

        return this._highlightLayer;
    }

    createMeasureLayer() {
        this._measureLayer = new VectorLayer({
            id: 'measure',
            source: new VectorSource(),
        });

        return this._measureLayer;
    }

    createTempLayer() {

        let style = new Style({
            image: createCircle(
                5, 'rgba(0, 128, 255, 0.5)', 3, 'rgba(0, 128, 255, 0.5)', {}
            ),
            stroke: new Stroke({
                color: 'rgba(0, 128, 255, 0.5)',
                width: 5
            }),
            fill: new Fill({
                color: 'rgba(0, 128, 255, 0.2)'
            })
        });

        this._tempLayer = new VectorLayer({
            id: '_TEMP_',
            source: new VectorSource(),
            //style: style
            style: this.createTempLayerStyles
        });

        return this._tempLayer;
    }

    createTempLayerStyles(feature) {
        let styles = [];

        styles.push(new Style({
            image: createCircle(
                5, 'rgba(0, 128, 255, 0.5)', 3, 'rgba(0, 128, 255, 0.5)', {}
            ),
            stroke: new Stroke({
                color: 'rgba(0, 128, 255, 0.5)',
                width: 5
            }),
            fill: new Fill({
                color: 'rgba(0, 128, 255, 0.2)'
            })
        }));

        const geometry = feature.getGeometry();
        if (geometry.getType() === 'Polygon') {
            let coords = geometry.getCoordinates()[0];
            for (let i = 1; i < coords.length; i++) {
                styles.push(new Style({
                    geometry: new Point(coords[i]),
                    image: createCircle(
                        2, 'rgba(0, 100, 255)', 1, 'rgba(0, 100, 255)'
                    ),
                }));
            }
        }

        return styles;
    }

    createLayer(layerDefinition) {
        return createLayer(layerDefinition);
    }

    createView(mapProjectDefinition, viewState) {
        let options = {
            ...viewState,
            projection: createProjection(mapProjectDefinition.projection),
            resolutions: mapProjectDefinition.resolutions,
            minZoom: mapProjectDefinition.minZoom,
            maxZoom: mapProjectDefinition.maxZoom
        };

        this._view = new View(options);

        this._view.on('propertychange',
            (e) => {
                switch (e.key) {
                    case 'center':
                        this.changeView(e.target.get(e.key), null, null, null);
                        break;
                    case 'resolution':
                        this.changeView(
                            null,
                            this._view.getZoomForResolution(e.target.get(e.key)),
                            null,
                            e.target.get(e.key)
                        );
                        break;
                    case 'rotation':
                        this.changeView(null, null, e.target.get(e.key), null);
                        break;
                    default:
                        break;
                }
            }
        );
    }

    createMap(mapContainer, mapProjectDefinition, viewState) {
        this.createView(mapProjectDefinition, viewState);

        this._map = new Map({
            target: mapContainer,
            //keyboardEventTarget: mapContainer,
            controls: [],
            interactions: this.getInteractionsDefault(),
            layers: [
                ...createLayers(mapProjectDefinition.layers),
                this.createHighlightLayer(),
                this.createTempLayer(),
                this.createMeasureLayer(),
            ],
            view: this._view
        });

        this.addInteractions();

        this.changeLayerVisibility(viewState.layers);

        if (viewState.defaultExtent) {
            this.fit(viewState.defaultExtent);
        }
    }

    changeLayerVisibility(virtualLayers) {
        let layers = {};

        virtualLayers.forEach(virtualLayer => {
            virtualLayer.layers.forEach(item => {
                layers[item] = layers[item] || virtualLayer.visible;
            })
        });

        this._map.getLayers().forEach(layer => {
            if (isBoolean(layers[layer.get('id')])) {
                layer.setVisible(layers[layer.get('id')] || false);
            }
        });
    }

    changeView(center, zoom, rotation, resolution) {
        if (this._options.onChangeView) {
            this._options.onChangeView(center, zoom, rotation, resolution);
        }
    }

    fit(extent) {
        this._view.fit(extent);
    }

    getFeaturesAtPixel(pixel) {
        let features = [];
        this._map.forEachFeatureAtPixel(pixel, (feature) => {
            return features.push(feature);
        });
        return features;
    }

    getFeaturesFromEvent(evt) {
        let pixel = this._map.getEventPixel(evt.originalEvent);
        let features = this.getFeaturesAtPixel(pixel);
        return features;
    }

    getHighlightLayer() {
        return this._highlightLayer;
    }

    getInteractionsDefault() {
        let interactions = defaultInteractions({
            keyboard: false,
            shiftDragZoom : false
        });
        return interactions;
    }

    getMap() {
        return this._map;
    }

    getMeasureLayer() {
        return this._measureLayer;
    }

    getTempLayer() {
        return this._tempLayer;
    }

    getView() {
        return this._view;
    }

    setCenter(center) {
        this._view.setCenter(center);
    }

    setZoom(zoom) {
        this._view.setZoom(zoom);
    }
}